import React, { useEffect, useState } from "react";
import {
  Box, Grid, Card, CardContent, Typography, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Select, MenuItem, FormControl, InputLabel,
} from "@mui/material";
import adminApi from "../api/adminApi";

const STATUS_COLORS = {
  success: "success",
  failed: "error",
  payment_captured_pending_submission: "warning",
};

const StatCard = ({ label, value, color = "#1C2541" }) => (
  <Card elevation={1} sx={{ borderRadius: 3 }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h4" fontWeight="bold" color={color}>{value}</Typography>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [statsRes, listRes] = await Promise.all([
      adminApi.get("/reservations/stats"),
      adminApi.get(`/reservations${statusFilter ? `?status=${statusFilter}` : ""}`),
    ]);
    setStats(statsRes.data);
    setReservations(listRes.data.reservations);
    setLoading(false);
  };

  useEffect(() => { load(); }, [statusFilter]);

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="#1C2541" mb={3}>Dashboard</Typography>

      <Grid container spacing={2} mb={4}>
        <Grid item xs={6} md={3}><StatCard label="Total Bookings" value={stats.total} /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Confirmed" value={stats.success} color="#2e7d32" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Failed" value={stats.failed} color="#c62828" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Total Revenue" value={`€${stats.revenue.toFixed(2)}`} color="#EE8013" /></Grid>
      </Grid>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6" fontWeight="bold" color="#1C2541">Recent Bookings</Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Filter by status</InputLabel>
          <Select value={statusFilter} label="Filter by status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="success">Confirmed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="payment_captured_pending_submission">Pending</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 3 }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f0f2f5" }}>
            <TableRow>
              <TableCell><strong>Customer</strong></TableCell>
              <TableCell><strong>Vehicle</strong></TableCell>
              <TableCell><strong>Pick-up</strong></TableCell>
              <TableCell><strong>Return</strong></TableCell>
              <TableCell><strong>Value</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reservations.map((r) => (
              <TableRow key={r._id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{r.customerName}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.customerEmail}</Typography>
                </TableCell>
                <TableCell>{r.vehicleModel || r.vehicleCode}</TableCell>
                <TableCell>
                  <Typography variant="body2">{r.pickupDate}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.pickUpDesk}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{r.returnDate}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.dropOffDesk}</Typography>
                </TableCell>
                <TableCell>€{r.totalRentalValue}</TableCell>
                <TableCell>
                  <Chip label={r.status === "success" ? "Confirmed" : r.status === "failed" ? "Failed" : "Pending"}
                    color={STATUS_COLORS[r.status]} size="small" />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{new Date(r.createdAt).toLocaleDateString()}</Typography>
                </TableCell>
              </TableRow>
            ))}
            {reservations.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center"><Typography color="text.secondary" py={3}>No bookings found</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Dashboard;
